import React from "react";

import { Alert, Button, Card, Col, Empty, Flex, Modal, Row, Spin, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import RateModel from "../../viewModels/Rate/RateModel";
import { makeSelectCurrencies, makeSelectLanguage, makeSelectUsers } from "../../containers/App/selectors";
import formatPrice from "../../utils/formatPrice";
import { useAppToken } from "../../theme/v6/useAppToken";
import RateIncludedService from "../../components/Calculations/components/FtlCalculation/components/RateIncludedService";
import RateCargoIcons from "../../components/RateCargoIcons";
import RateInfo from "../../components/RateInfo";
import useInjectSaga from "../../utils/useInjectSaga";
import slice from "./reducer";
import saga from "./saga";
import {
  makeSelectActiveTab,
  makeSelectCarrierName,
  makeSelectCalculationVehicleId,
  makeSelectCreateError,
  makeSelectCreatingRateId,
  makeSelectError,
  makeSelectItems,
  makeSelectLoading,
  makeSelectLoadingMore,
  makeSelectOpen,
  makeSelectTotal,
} from "./selectors";
import { RateHistoryTab } from "./types";

type RateHistoryItemProps = {
  rate: RateModel;
  creatingRateId?: number;
  onCreateWishElementByRate?: (rateId: number) => void;
};

function RateServices({ rate }: { rate: RateModel }) {
  const { t } = useTranslation("translations");

  return (
    <Flex vertical>
      <Flex gap={32}>
        <RateIncludedService label={t("SVH")} value={rate.hasSvh} />
        <RateIncludedService label={t("PRR")} value={rate.hasPrr} />
      </Flex>
      <RateIncludedService label={t("forwarding")} value={rate.hasExpedition} />
      <RateIncludedService label={t("insurance")} value={rate.hasInsurance} />
      <RateIncludedService label={t("registration_of_transit_declaration")} value={rate.hasTransitDeclaration} />
      <RateIncludedService label={t("export_declaration")} value={rate.hasExportDeclaration} />
    </Flex>
  );
}

const RateHistoryItem = React.memo(({ rate, creatingRateId, onCreateWishElementByRate }: RateHistoryItemProps) => {
  const { t } = useTranslation("translations");
  const currencies = useSelector(makeSelectCurrencies());
  const users = useSelector(makeSelectUsers());
  const lang = useSelector(makeSelectLanguage());

  const { token } = useAppToken();

  const currency = currencies.find((x) => x.id === rate.currencyId);
  const creator = users.find((x) => x.id === rate.creatorId);
  const isValidRate = rate.dateValidTo.diff(dayjs(), "day") >= 0;
  const creating = creatingRateId === rate.id;

  const handleCreateWishElementByRate = React.useCallback(() => {
    onCreateWishElementByRate?.(rate.id);
  }, [onCreateWishElementByRate, rate.id]);

  return (
    <Card size="small" style={{ boxShadow: token.appCardShadow, marginBottom: 12 }}>
      <Row gutter={16} align="middle">
        <Col span={12}>
          <RateCargoIcons rate={rate} />
          <RateServices rate={rate} />
        </Col>
        <Col span={12}>
          <Flex justify="end" gap={16}>
            <Flex vertical align="flex-end">
              <Flex align="center" gap={4}>
                <RateInfo applicationId={rate.applicationId} calculationId={rate.applicationCalculationId} />
                <Typography.Text strong style={{ color: isValidRate ? token.green : token.orange }}>
                  {formatPrice(rate.rate, currency?.symbol)}
                </Typography.Text>
              </Flex>
              <Typography.Text style={{ fontSize: 12, color: token.colorTextSecondary }}>
                {t("actual_till", { date: rate.dateValidTo.locale(lang).format("DD.MM.YY") })}
              </Typography.Text>
              <Typography.Text style={{ fontSize: 12, color: token.colorTextSecondary }}>
                {t("updated")} {rate.dateUpdatedAt.locale(lang).format("DD.MM.YY")}
              </Typography.Text>
              <Typography.Text style={{ fontSize: 12, color: token.colorTextSecondary }}>{creator?.fullName}</Typography.Text>
            </Flex>
            {onCreateWishElementByRate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                loading={creating}
                disabled={!!creatingRateId && !creating}
                onClick={handleCreateWishElementByRate}
              />
            )}
          </Flex>
        </Col>
      </Row>
    </Card>
  );
});

const RateHistoryModal = React.memo(() => {
  const { t } = useTranslation("translations");
  const { token } = useAppToken();

  useInjectSaga("rateHistoryModal", saga);

  const dispatch = useDispatch();

  const open = useSelector(makeSelectOpen);
  const activeTab = useSelector(makeSelectActiveTab);
  const carrierName = useSelector(makeSelectCarrierName);
  const calculationVehicleId = useSelector(makeSelectCalculationVehicleId);
  const rates = useSelector(makeSelectItems);
  const total = useSelector(makeSelectTotal);
  const loading = useSelector(makeSelectLoading);
  const loadingMore = useSelector(makeSelectLoadingMore);
  const error = useSelector(makeSelectError);
  const createError = useSelector(makeSelectCreateError);
  const creatingRateId = useSelector(makeSelectCreatingRateId);

  const listRef = React.useRef<HTMLDivElement>(null);
  const hasMore = rates.length < total;

  React.useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [activeTab]);

  const handleClose = React.useCallback(() => {
    dispatch(slice.actions.closeModal());
  }, [dispatch]);

  const handleChangeTab = React.useCallback(
    (tab: RateHistoryTab) => {
      if (activeTab !== tab) {
        dispatch(slice.actions.changeTab(tab));
      }
    },
    [activeTab, dispatch],
  );

  const handleRetry = React.useCallback(() => {
    dispatch(slice.actions.retryLoad());
  }, [dispatch]);

  const handleCreateWishElementByRate = React.useCallback(
    (rateId: number) => {
      dispatch(slice.actions.createWishElementByRate({ rateId }));
    },
    [dispatch],
  );

  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 48;

      if (nearBottom && hasMore && !loading && !loadingMore && !error) {
        dispatch(slice.actions.loadMore());
      }
    },
    [dispatch, error, hasMore, loading, loadingMore],
  );

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      footer={
        <Button style={{ width: "100%" }} onClick={handleClose}>
          {t("back")}
        </Button>
      }
      title={
        <Typography.Text strong>
          <Typography.Text strong style={{ color: token.colorPrimary }}>
            {t("rate_history_title")}
          </Typography.Text>{" "}
          {carrierName}
        </Typography.Text>
      }
      width={940}
    >
      <Flex vertical gap={16}>
        <Flex style={{ width: "100%" }}>
          <Button
            type={activeTab === "SELECTED_SERVICES" ? "primary" : "default"}
            ghost={activeTab === "SELECTED_SERVICES"}
            style={{ width: "50%" }}
            onClick={() => handleChangeTab("SELECTED_SERVICES")}
          >
            {t("rate_history_selected_services")}
          </Button>
          <Button
            type={activeTab === "DIFFERENT_SERVICES" ? "primary" : "default"}
            ghost={activeTab === "DIFFERENT_SERVICES"}
            style={{ width: "50%" }}
            onClick={() => handleChangeTab("DIFFERENT_SERVICES")}
          >
            {t("rate_history_different_services")}
          </Button>
        </Flex>

        {createError && <Alert type="error" showIcon title={t("error")} />}

        <Card size="small" styles={{ body: { background: token.appGray2, padding: "12px 8px" } }}>
          <Row style={{ padding: "0 8px 8px" }}>
            <Col span={12}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t("included_in_the_rate")}
              </Typography.Text>
            </Col>
            <Col span={12}>
              <Flex justify="end" style={{ paddingRight: calculationVehicleId ? 88 : 0 }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t("bid_for_one_car")}
                </Typography.Text>
              </Flex>
            </Col>
          </Row>
          <div ref={listRef} onScroll={handleScroll} style={{ height: 430, overflow: "auto", padding: "0 4px" }}>
            {loading ? (
              <Flex justify="center" align="center" style={{ height: "100%" }}>
                <Spin />
              </Flex>
            ) : error && rates.length === 0 ? (
              <Flex vertical justify="center" align="center" gap={12} style={{ height: "100%" }}>
                <Typography.Text type="secondary">{t("error")}</Typography.Text>
                <Button onClick={handleRetry}>{t("update")}</Button>
              </Flex>
            ) : rates.length === 0 ? (
              <Flex justify="center" align="center" style={{ height: "100%" }}>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </Flex>
            ) : (
              <>
                {rates.map((rate) => (
                  <RateHistoryItem
                    key={rate.id}
                    rate={rate}
                    creatingRateId={creatingRateId}
                    onCreateWishElementByRate={calculationVehicleId ? handleCreateWishElementByRate : undefined}
                  />
                ))}
                {loadingMore && (
                  <Flex justify="center" style={{ padding: 8 }}>
                    <Spin size="small" />
                  </Flex>
                )}
                {error && rates.length > 0 && (
                  <Flex justify="center" style={{ padding: 8 }}>
                    <Button size="small" onClick={handleRetry}>
                      {t("update")}
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </div>
        </Card>
      </Flex>
    </Modal>
  );
});

export default RateHistoryModal;
